/**
 * The pipeline board's logic, kept pure so it can be tested without the screen. From the
 * Pipedrive deals research (Oct 2026):
 * - deals are sized in doors, never dollars (the value was $0 on every deal);
 * - a deal is flagged once it's slower than 3 in 4 deals were (each stage's `limitDays`);
 * - the account says who the client is: type, city, doors, "existing client", next step.
 */
import type { Identifier } from "ra-core";

import type { ZcrmAccount } from "../providers/zinspector/accounts";
import { QUIET_AFTER_DAYS, STAGES } from "../providers/zinspector/crmConfig";
import type { ZcrmDeal } from "../providers/zinspector/mappers";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The stages that are board columns: Won, Lost and Snoozed are drop areas instead. */
type Stage = (typeof STAGES)[number];
export type OpenStage = Exclude<Stage, { end: true }>;
export const OPEN_STAGES = STAGES.filter(
  (s): s is OpenStage => !("end" in s && s.end),
);

export type PipelineId = "osp" | "mywalkthru";
export const PIPELINES: { id: PipelineId; label: string }[] = [
  { id: "osp", label: "Sales · OSP" },
  { id: "mywalkthru", label: "Sales · MWT" },
];

/** MyWalkThru deals have their own board; everything else is OSP. */
export const pipelineOf = (deal: Pick<ZcrmDeal, "product">): PipelineId =>
  deal.product === "mywalkthru" ? "mywalkthru" : "osp";

export type BoardDeal = ZcrmDeal & {
  account_name: string;
  account_kind: string;
  city: string;
  doors: number | null;
  is_existing_client: boolean;
  days_in_stage: number;
  limit_days: number | null;
  is_stalled: boolean;
  last_touch: string;
  days_since_touch: number;
  is_quiet: boolean;
  is_unassigned: boolean;
  next_step: string;
  next_step_due: string | null;
};

const daysBetween = (from: string | null | undefined, today: Date) => {
  const time = from ? Date.parse(from) : NaN;
  return Number.isNaN(time)
    ? 0
    : Math.max(0, Math.floor((today.getTime() - time) / DAY_MS));
};

const latest = (dates: (string | null | undefined)[]) =>
  dates
    .filter((d): d is string => !!d)
    .sort()
    .at(-1) ?? "";

export const toBoardDeal = (
  deal: ZcrmDeal,
  account: ZcrmAccount | undefined,
  today = new Date(),
): BoardDeal => {
  const stage = STAGES.find((s) => s.id === deal.stage);
  const limit = stage?.limitDays ?? null;
  const days = daysBetween(deal.stage_since || deal.updated_at, today);
  // The last thing that happened: the deal changed, or a call, meeting or note on the account.
  const lastTouch = latest([deal.updated_at, account?.last_touch]);
  const sinceTouch = daysBetween(lastTouch, today);
  return {
    ...deal,
    // A deal converted from a lead with no account yet carries its own name and doors.
    account_name: account?.name ?? (deal.company_name || deal.name),
    account_kind: account?.account_kind ?? "",
    city: [account?.city, account?.state_abbr].filter(Boolean).join(", "),
    doors: account ? account.doors_total : deal.amount || null,
    is_existing_client: !!account && account.view !== "prospects",
    days_in_stage: days,
    limit_days: limit,
    is_stalled: limit !== null && days > limit,
    last_touch: lastTouch,
    days_since_touch: sinceTouch,
    is_quiet: sinceTouch >= QUIET_AFTER_DAYS,
    is_unassigned: !deal.sales_id,
    next_step: account?.next_step ?? "",
    next_step_due: account?.next_step_due ?? null,
  };
};

export type ChipId = "all" | "unassigned" | "stalled" | "quiet" | "mine";

export const CHIPS: { id: ChipId; label: string; help: string }[] = [
  { id: "all", label: "All deals", help: "Every open deal in this pipeline" },
  {
    id: "unassigned",
    label: "Unassigned",
    help: "New leads nobody owns yet (most come from the chatbot)",
  },
  {
    id: "stalled",
    label: "Past stage time",
    help: "Slower than 3 in 4 deals were in Pipedrive",
  },
  {
    id: "quiet",
    label: `Gone quiet ${QUIET_AFTER_DAYS}d+`,
    help: `No change, call, meeting or note for ${QUIET_AFTER_DAYS} days or more`,
  },
  { id: "mine", label: "Mine", help: "Deals I own" },
];

export const matchesChip = (
  deal: BoardDeal,
  chip: ChipId,
  me?: Identifier,
): boolean => {
  switch (chip) {
    case "unassigned":
      return deal.is_unassigned;
    case "stalled":
      return deal.is_stalled;
    case "quiet":
      return deal.is_quiet;
    case "mine":
      return me != null && String(deal.sales_id) === String(me);
    default:
      return true;
  }
};

export const matchesSearch = (deal: BoardDeal, search: string): boolean => {
  const text = search.trim().toLowerCase();
  if (!text) return true;
  return [deal.name, deal.account_name, deal.city, deal.early_contact_name]
    .join(" ")
    .toLowerCase()
    .includes(text);
};

/** A column header's numbers: deals and doors, and how many are past the limit. */
export const summarizeStage = (deals: BoardDeal[]) => ({
  count: deals.length,
  doors: deals.reduce((sum, d) => sum + (d.doors ?? 0), 0),
  late: deals.filter((d) => d.is_stalled).length,
});

/** The list view's order: the most overdue (relative to its stage's limit) first. */
export const byUrgency = (a: BoardDeal, b: BoardDeal): number =>
  b.days_in_stage / (b.limit_days ?? Infinity) -
  a.days_in_stage / (a.limit_days ?? Infinity);

export const formatDoors = (doors: number | null): string =>
  doors ? `${doors.toLocaleString("en-US")} doors` : "Doors not set";

/** The board's views: open deals, or the ones that left it. */
export type Outcome = "open" | "won" | "lost" | "snoozed";

/** A snoozed deal whose date has passed: zInspector won't wake it, so the list flags it. */
export const isReadyToWake = (
  deal: Pick<ZcrmDeal, "stage" | "expected_closing_date">,
  today = new Date(),
): boolean =>
  deal.stage === "snoozed" &&
  !!deal.expected_closing_date &&
  Date.parse(deal.expected_closing_date) <= today.getTime();

export interface FunnelStage {
  id: string;
  label: string;
  /** Deals that got at least this far. */
  reached: number;
  /** Deals open in this stage right now. */
  open: number;
  /** Deals lost (or snoozed) while in this stage. */
  lost: number;
  snoozed: number;
  /** Share of the deals that reached this stage and went on to the next (or to Won). */
  conversion: number | null;
  /** Loss reasons at this stage, most common first. */
  reasons: { reason: string; count: number }[];
}

export interface Funnel {
  stages: FunnelStage[];
  won: number;
  /** Closed deals with no recorded stage (closed in the dashboard, or before zCRM). */
  unknown: number;
}

/**
 * Where deals drop. A deal's furthest stage is its current one if open, the last stage if won,
 * and the stage it was lost or snoozed from (recorded by zCRM) otherwise.
 */
export const buildFunnel = (deals: ZcrmDeal[]): Funnel => {
  const index = new Map<string, number>(OPEN_STAGES.map((s, i) => [s.id, i]));
  const last = OPEN_STAGES.length - 1;
  let won = 0;
  let unknown = 0;
  const furthest: { at: number; deal: ZcrmDeal }[] = [];
  for (const deal of deals) {
    if (deal.stage === "won") {
      won += 1;
      furthest.push({ at: last, deal });
    } else if (deal.stage === "lost" || deal.stage === "snoozed") {
      const at = index.get(deal.closed_from);
      if (at === undefined) unknown += 1;
      else furthest.push({ at, deal });
    } else if (index.has(deal.stage)) {
      furthest.push({ at: index.get(deal.stage)!, deal });
    }
  }
  const stages = OPEN_STAGES.map((stage, i) => {
    const here = furthest.filter((f) => f.at === i).map((f) => f.deal);
    const lostHere = here.filter((d) => d.stage === "lost");
    const counts = new Map<string, number>();
    for (const d of lostHere) {
      const reason = d.loss_reason || "No reason";
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return {
      id: stage.id,
      label: stage.label,
      reached: furthest.filter((f) => f.at >= i).length,
      open: here.filter((d) => d.stage === stage.id).length,
      lost: lostHere.length,
      snoozed: here.filter((d) => d.stage === "snoozed").length,
      conversion: null as number | null,
      reasons: [...counts.entries()]
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count),
    };
  });
  stages.forEach((stage, i) => {
    const next = i < last ? stages[i + 1].reached : won;
    stage.conversion = stage.reached ? next / stage.reached : null;
  });
  return { stages, won, unknown };
};
